import { AnalyticsContext } from '@carvana/eventable-clickstream';
import MerchTile, {
  type CoreData,
  type TileAvailabilityInfo,
  type FinanceData,
  type TrackEvent,
  BottomPaddingForBoxShadow
} from '@carvana/merch-tile';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import {
  useFavoritesIds,
  useUserLocation,
  useIsLoggedIn
} from '~/context/common/UserContext';
import { useToggleFavorite } from '~/hooks/common/favorites';

type OwnProps = {
  availabilityInfo?: TileAvailabilityInfo;
  financeInfo?: FinanceData;
  handleTileClick?: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => void;
  hasSoftPull: boolean;
  isTradeInApplied: boolean;
  vehicle: CoreData;
};

export default function VehicleTile({
  availabilityInfo,
  financeInfo,
  handleTileClick,
  hasSoftPull,
  isTradeInApplied,
  vehicle
}: OwnProps) {
  const favoriteIds = useFavoritesIds();
  const userLocation = useUserLocation();
  const { trackEvent } = useContext(AnalyticsContext);
  const toggleFavorite = useToggleFavorite();
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicle.vehicleId)
  );

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { vehicleId } = vehicle;

      toggleFavorite({ vehicleId });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Ev Tiles favorites cta', {
        vehicleId,
        action: isFavoritedLocally ? 'remove' : 'add'
      });
    },
    [isFavoritedLocally, isLoggedIn, toggleFavorite, trackEvent, vehicle]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  return (
    <BottomPaddingForBoxShadow>
      <MerchTile
        data-testid="Eligible Ev Tile"
        assets={{
          cvnaVexFastlyImageUrl: vexCdnUrl,
          cvnaAppAssetsCdn: assetsCdnUrl
        }}
        availabilityInfo={availabilityInfo}
        coreData={vehicle}
        financeInfo={financeInfo}
        hasBorder
        isFavorite={isFavoritedLocally}
        showFinanceInfo={true}
        showTermsTooltip={false}
        userInfo={{
          locationState: userLocation.state,
          hasSoftPull: hasSoftPull,
          tradeInApplied: isTradeInApplied
        }}
        utils={{
          handleFavoriteClick: handleFavorite,
          handleTileClick,
          trackEvent: trackEvent as TrackEvent
        }}
      />
    </BottomPaddingForBoxShadow>
  );
}
